import { useAuth0 } from '@auth0/auth0-react';
import React, {
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import api from '../../api';
import PRESETS from './aiCallPresets';

/**
 * Dev test page for the AI outbound calling rig.
 *
 *   POST /ai-calls/initiate  ← from this form
 *   GET  /ai-calls/:id       ← polled every 2s while a call is active
 *
 * Mounted at /ai-call-test in App.tsx. Uses the logged-in user's Auth0
 * token automatically — no need to copy JWTs by hand.
 */

interface InitiateResponse {
  callId: string;
  twilioCallSid: string;
  status: string;
  twimlUrl: string;
  streamUrl: string;
}

interface CallRow {
  id: string;
  recipientPhone: string;
  recipientName: string | null;
  purpose: string;
  status: string;
  outcome: string | null;
  twilioStatus: string | null;
  twilioErrorMessage: string | null;
  summary: string | null;
  transcript: string | null;
  durationSeconds: string | null;
  toolCallCount: number;
  ragInjectionCount: number;
  systemPromptLength: number | null;
  connectedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  structuredData: Record<string, unknown> | null;
  timings: Record<string, number> | null;
}

const VOICE_OPTIONS = [
  '',
  'cedar',
  'marin',
  'alloy',
  'verse',
  'ballad',
  'coral',
  'sage',
  'ash',
] as const;

const TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'no_answer',
  'busy',
  'canceled',
  'escalated',
]);

const AiCallTester: FC = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // ── Form state ────────────────────────────────────────────────────────
  const [phoneNumber, setPhoneNumber] = useState('+44');
  const [recipientName, setRecipientName] = useState('');
  const [recipientType, setRecipientType] = useState('');
  const [purpose, setPurpose] = useState('');
  const [scriptGuidance, setScriptGuidance] = useState('');
  const [expectedOutcomesRaw, setExpectedOutcomesRaw] = useState('');
  const [contingenciesRaw, setContingenciesRaw] = useState('');
  const [language, setLanguage] = useState('English');
  const [firstMessage, setFirstMessage] = useState('');
  const [voice, setVoice] = useState<string>('');

  // ── Tuning overrides ────────────────────────────────────────────────
  const [showTuning, setShowTuning] = useState(false);
  const [vadSilenceDurationMs, setVadSilenceDurationMs] = useState('700');
  const [vadThreshold, setVadThreshold] = useState('0.5');
  const [vadPrefixPaddingMs, setVadPrefixPaddingMs] = useState('300');
  const [greetingFallbackMs, setGreetingFallbackMs] = useState('3000');
  const [hangupGraceMs, setHangupGraceMs] = useState('2500');
  const [ragBudgetMs, setRagBudgetMs] = useState('700');
  const [transcriptionModel, setTranscriptionModel] = useState(
    'gpt-realtime-whisper',
  );
  const [minTurnsBeforeEnd, setMinTurnsBeforeEnd] = useState('0');
  const [adaptive, setAdaptive] = useState(true);

  // ── Submit + live state ───────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [callRow, setCallRow] = useState<CallRow | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);

  // ── Recent calls list ─────────────────────────────────────────────────
  const [recent, setRecent] = useState<CallRow[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Helpers ───────────────────────────────────────────────────────────
  const fetchRecent = useCallback(async () => {
    if (!isAuthenticated) return;
    setRecentLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<{ calls: CallRow[] }>('/ai-calls', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecent(res.data.calls || []);
    } catch (err) {
      // Non-fatal — recent list is convenience only.
      console.warn('[AiCallTester] Failed to load recent calls', err);
    } finally {
      setRecentLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  // ── Polling loop while a call is active ───────────────────────────────
  useEffect(() => {
    if (!activeCallId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<{ call: CallRow }>(
          `/ai-calls/${activeCallId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setCallRow(res.data.call);
        setPollError(null);

        if (TERMINAL_STATUSES.has(res.data.call.status)) {
          // One last refresh of the recent list, then stop polling.
          fetchRecent();
          return;
        }
      } catch (err: any) {
        if (cancelled) return;
        setPollError(err?.message || 'Polling failed');
      }

      pollTimerRef.current = setTimeout(poll, 2000);
    };

    poll();

    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [activeCallId, getAccessTokenSilently, fetchRecent]);

  // ── Submit ────────────────────────────────────────────────────────────
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setCallRow(null);

    const expectedOutcomes = expectedOutcomesRaw
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    // Parse contingencies: each line is "if X → then Y" or "If X → Y"
    const contingencies = contingenciesRaw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/^(?:if\s+)?(.+?)\s*(?:→|⇒|->)+\s*(.+)$/i);
        if (match) return { if: match[1].trim(), then: match[2].trim() };
        return { if: line, then: '(handle appropriately)' };
      });

    if (!phoneNumber.trim()) {
      setSubmitError('Phone number is required.');
      return;
    }
    if (!purpose.trim()) {
      setSubmitError('Purpose is required.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();

      // Build tuning object — only include values that differ from defaults
      const tuning: Record<string, number | string | boolean> = {};
      if (vadSilenceDurationMs !== '700')
        tuning.vadSilenceDurationMs = Number(vadSilenceDurationMs);
      if (vadThreshold !== '0.5') tuning.vadThreshold = Number(vadThreshold);
      if (vadPrefixPaddingMs !== '300')
        tuning.vadPrefixPaddingMs = Number(vadPrefixPaddingMs);
      if (greetingFallbackMs !== '3000')
        tuning.greetingFallbackMs = Number(greetingFallbackMs);
      if (hangupGraceMs !== '2500')
        tuning.hangupGraceMs = Number(hangupGraceMs);
      if (ragBudgetMs !== '700') tuning.ragBudgetMs = Number(ragBudgetMs);
      if (transcriptionModel !== 'gpt-realtime-whisper')
        tuning.transcriptionModel = transcriptionModel;
      if (minTurnsBeforeEnd !== '0')
        tuning.minTurnsBeforeEnd = Number(minTurnsBeforeEnd);
      if (adaptive) tuning.adaptive = true;

      const res = await api.post<InitiateResponse>(
        '/ai-calls/initiate',
        {
          phoneNumber: phoneNumber.trim(),
          recipientName: recipientName.trim() || undefined,
          recipientType: recipientType.trim() || undefined,
          purpose: purpose.trim(),
          scriptGuidance: scriptGuidance.trim() || undefined,
          expectedOutcomes:
            expectedOutcomes.length > 0 ? expectedOutcomes : undefined,
          contingencies: contingencies.length > 0 ? contingencies : undefined,
          language: language !== 'English' ? language : undefined,
          firstMessage: firstMessage.trim() || undefined,
          voice: voice || undefined,
          tuning: Object.keys(tuning).length > 0 ? tuning : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setActiveCallId(res.data.callId);
      fetchRecent();
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to initiate call',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    setActiveCallId(null);
    setCallRow(null);
    setPollError(null);
    setSubmitError(null);
  };

  const isTerminal = callRow ? TERMINAL_STATUSES.has(callRow.status) : false;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.h1}>AI Outbound Call Tester</h1>
        <p style={styles.subtitle}>
          Dev rig for placing real outbound calls via Twilio +{' '}
          <code>gpt-realtime-2</code>. Inherits your secretary’s persona &amp;
          memory.
        </p>
      </div>

      <div style={styles.grid}>
        {/* ── LEFT: form ───────────────────────────────────────────────── */}
        <form onSubmit={handleSubmit} style={styles.card}>
          <h2 style={styles.h2}>1 · Configure call</h2>

          <Field
            label='Scenario preset'
            hint='Quick-fill fields from a template (does not change phone or tuning)'
          >
            <select
              value=''
              onChange={(e) => {
                const preset = PRESETS[Number(e.target.value)];
                if (!preset) return;
                setRecipientName(preset.recipientName);
                setRecipientType(preset.recipientType);
                setPurpose(preset.purpose);
                setScriptGuidance(preset.scriptGuidance);
                setExpectedOutcomesRaw(
                  preset.expectedOutcomes || preset.endConditions || '',
                );
                setContingenciesRaw(preset.contingencies ?? '');
                setLanguage(preset.language ?? 'English');
              }}
              style={styles.input}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            >
              <option value=''>— Choose a preset scenario —</option>
              {PRESETS.map((p, i) => (
                <option key={i} value={i}>
                  {p.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label='Phone number (E.164)' required>
            <input
              type='tel'
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder='+44 7700 900123'
              style={styles.input}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            />
          </Field>

          <div style={styles.row}>
            <Field label='Recipient name' style={{ flex: 1 }}>
              <input
                type='text'
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder='Padella'
                style={styles.input}
                disabled={submitting || (!!activeCallId && !isTerminal)}
              />
            </Field>
            <Field label='Recipient type' style={{ flex: 1 }}>
              <input
                type='text'
                value={recipientType}
                onChange={(e) => setRecipientType(e.target.value)}
                placeholder='restaurant / dentist / supplier'
                style={styles.input}
                disabled={submitting || (!!activeCallId && !isTerminal)}
              />
            </Field>
          </div>

          <Field label='Purpose' required>
            <textarea
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder='Book a table for 2 at Padella on Friday 17 May at 7pm under the name Petrenz.'
              style={{ ...styles.input, minHeight: 80, resize: 'vertical' }}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            />
          </Field>

          <Field
            label='Additional context (optional)'
            hint='Background notes for the agent — preferences, constraints, reference numbers. NOT a script.'
          >
            <textarea
              value={scriptGuidance}
              onChange={(e) => setScriptGuidance(e.target.value)}
              placeholder='Prefer outdoor seating. Verbal confirmation is fine.'
              style={{ ...styles.input, minHeight: 60, resize: 'vertical' }}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            />
          </Field>

          <Field
            label='Expected outcomes (one per line)'
            hint='Checklist of results to achieve. Agent works through ALL before calling endCall.'
          >
            <textarea
              value={expectedOutcomesRaw}
              onChange={(e) => setExpectedOutcomesRaw(e.target.value)}
              placeholder={
                'Table booked for 2 at 7:30pm Saturday\nAllergy noted on reservation\nConfirmation reference obtained'
              }
              style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            />
          </Field>

          <Field
            label='Contingencies (one per line: "If X → then Y")'
            hint='How to handle deviations from the ideal outcome.'
          >
            <textarea
              value={contingenciesRaw}
              onChange={(e) => setContingenciesRaw(e.target.value)}
              placeholder={
                'If 7:30 unavailable → accept any time between 7-9pm\nIf fully booked Saturday → ask about Friday or Sunday instead'
              }
              style={{ ...styles.input, minHeight: 70, resize: 'vertical' }}
              disabled={submitting || (!!activeCallId && !isTerminal)}
            />
          </Field>

          <div style={styles.row}>
            <Field
              label='Language'
              hint='Language for call agent to open with. Can switch mid-call.'
              style={{ flex: 1 }}
            >
              <input
                type='text'
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder='English'
                style={styles.input}
                disabled={submitting || (!!activeCallId && !isTerminal)}
              />
            </Field>
          </div>

          <div style={styles.row}>
            <Field
              label='First message'
              hint='≤ 5 words ideal. Empty = model improvises.'
              style={{ flex: 2 }}
            >
              <input
                type='text'
                value={firstMessage}
                onChange={(e) => setFirstMessage(e.target.value)}
                placeholder='Hi, good afternoon'
                style={styles.input}
                disabled={submitting || (!!activeCallId && !isTerminal)}
              />
            </Field>
            <Field label='Voice' style={{ flex: 1 }}>
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                style={styles.input}
                disabled={submitting || (!!activeCallId && !isTerminal)}
              >
                {VOICE_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v || 'auto (cedar♂ / marin♀)'}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {/* ── Tuning panel ────────────────────────────────────── */}
          <div
            style={{
              marginTop: 16,
              borderTop: '1px solid #f3f4f6',
              paddingTop: 12,
            }}
          >
            <button
              type='button'
              onClick={() => setShowTuning(!showTuning)}
              style={{
                ...styles.btn,
                ...styles.btnGhost,
                fontSize: 12,
                padding: '4px 10px',
              }}
            >
              {showTuning ? '▾ Hide tuning' : '▸ Show tuning'}
            </button>
            {showTuning && (
              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                }}
              >
                <Field
                  label='VAD silence (ms)'
                  hint='Turn-end silence threshold'
                >
                  <input
                    type='number'
                    value={vadSilenceDurationMs}
                    onChange={(e) => setVadSilenceDurationMs(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='VAD threshold (0–1)'
                  hint='Activation sensitivity'
                >
                  <input
                    type='number'
                    step='0.05'
                    value={vadThreshold}
                    onChange={(e) => setVadThreshold(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='VAD prefix padding (ms)'
                  hint='Audio before speech_started'
                >
                  <input
                    type='number'
                    value={vadPrefixPaddingMs}
                    onChange={(e) => setVadPrefixPaddingMs(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='Greeting fallback (ms)'
                  hint='Timer before fallback opener'
                >
                  <input
                    type='number'
                    value={greetingFallbackMs}
                    onChange={(e) => setGreetingFallbackMs(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='Hangup grace (ms)'
                  hint='Post-closing cancellation window'
                >
                  <input
                    type='number'
                    value={hangupGraceMs}
                    onChange={(e) => setHangupGraceMs(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='RAG budget (ms)'
                  hint='Per-turn memory retrieval budget'
                >
                  <input
                    type='number'
                    value={ragBudgetMs}
                    onChange={(e) => setRagBudgetMs(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='Transcription model'
                  hint='whisper-1 or gpt-realtime-whisper'
                >
                  <select
                    value={transcriptionModel}
                    onChange={(e) => setTranscriptionModel(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  >
                    <option value='whisper-1'>whisper-1</option>
                    <option value='gpt-realtime-whisper'>
                      gpt-realtime-whisper
                    </option>
                  </select>
                </Field>
                <Field label='Min turns before endCall' hint='0 = no guard'>
                  <input
                    type='number'
                    value={minTurnsBeforeEnd}
                    onChange={(e) => setMinTurnsBeforeEnd(e.target.value)}
                    style={styles.input}
                    disabled={submitting || (!!activeCallId && !isTerminal)}
                  />
                </Field>
                <Field
                  label='Adaptive tuning'
                  hint='Auto-adjust VAD based on barge-ins &amp; speaking pace'
                  style={{ gridColumn: '1 / -1' }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 14,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type='checkbox'
                      checked={adaptive}
                      onChange={(e) => setAdaptive(e.target.checked)}
                      disabled={submitting || (!!activeCallId && !isTerminal)}
                    />
                    Enable mid-call adaptive VAD (adjusts silence/threshold
                    based on conversation signals)
                  </label>
                </Field>
              </div>
            )}
          </div>

          {submitError && <div style={styles.error}>{submitError}</div>}

          <div style={styles.actions}>
            {!activeCallId ? (
              <button
                type='submit'
                disabled={submitting}
                style={{
                  ...styles.btn,
                  ...styles.btnPrimary,
                  opacity: submitting ? 0.5 : 1,
                }}
              >
                {submitting ? 'Dialling…' : '📞 Place call'}
              </button>
            ) : (
              <button
                type='button'
                onClick={handleReset}
                disabled={!isTerminal}
                style={{
                  ...styles.btn,
                  ...styles.btnSecondary,
                  opacity: isTerminal ? 1 : 0.5,
                }}
              >
                {isTerminal ? '↺ Place another call' : 'Call in progress…'}
              </button>
            )}
          </div>
        </form>

        {/* ── RIGHT: live status ──────────────────────────────────────── */}
        <div style={styles.card}>
          <h2 style={styles.h2}>2 · Live status</h2>
          {!activeCallId && (
            <div style={styles.emptyState}>
              No active call. Fill out the form and press <em>Place call</em>.
            </div>
          )}
          {activeCallId && callRow && (
            <CallStatusPanel call={callRow} pollError={pollError} />
          )}
          {activeCallId && !callRow && (
            <div style={styles.emptyState}>Connecting to call…</div>
          )}
        </div>
      </div>

      {/* ── Recent calls list ────────────────────────────────────────── */}
      <div style={styles.card}>
        <div style={styles.recentHeader}>
          <h2 style={styles.h2}>Recent calls</h2>
          <button
            type='button'
            onClick={fetchRecent}
            disabled={recentLoading}
            style={{ ...styles.btn, ...styles.btnGhost }}
          >
            {recentLoading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        {recent.length === 0 ? (
          <div style={styles.emptyState}>
            {recentLoading ? 'Loading…' : 'No calls yet.'}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>When</th>
                <th style={styles.th}>To</th>
                <th style={styles.th}>Purpose</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Outcome</th>
                <th style={styles.th}>Dur</th>
                <th style={styles.th}>Tools</th>
                <th style={styles.th}>RAG</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((row) => (
                <tr
                  key={row.id}
                  style={{
                    ...styles.tr,
                    cursor: 'pointer',
                    background: row.id === activeCallId ? '#fef3c7' : undefined,
                  }}
                  onClick={() => setActiveCallId(row.id)}
                  title='Load into live status panel'
                >
                  <td style={styles.td}>
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td style={styles.td}>
                    {row.recipientName ? `${row.recipientName} ` : ''}
                    <span style={{ color: '#666' }}>{row.recipientPhone}</span>
                  </td>
                  <td style={{ ...styles.td, ...styles.tdEllipsis }}>
                    {row.purpose}
                  </td>
                  <td style={styles.td}>
                    <StatusPill status={row.status} />
                  </td>
                  <td style={styles.td}>{row.outcome || '—'}</td>
                  <td style={styles.td}>
                    {row.durationSeconds
                      ? `${Number(row.durationSeconds).toFixed(0)}s`
                      : '—'}
                  </td>
                  <td style={styles.td}>{row.toolCallCount}</td>
                  <td style={styles.td}>{row.ragInjectionCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────

const Field: FC<{
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}> = ({ label, required, hint, children, style }) => (
  <div style={{ ...styles.field, ...style }}>
    <label style={styles.label}>
      {label}
      {required && <span style={{ color: '#dc2626' }}> *</span>}
    </label>
    {children}
    {hint && <div style={styles.hint}>{hint}</div>}
  </div>
);

const StatusPill: FC<{ status: string }> = ({ status }) => {
  const palette: Record<string, { bg: string; fg: string }> = {
    pending: { bg: '#e5e7eb', fg: '#374151' },
    dialing: { bg: '#dbeafe', fg: '#1e40af' },
    ringing: { bg: '#dbeafe', fg: '#1e40af' },
    in_progress: { bg: '#dcfce7', fg: '#166534' },
    completed: { bg: '#d1fae5', fg: '#065f46' },
    failed: { bg: '#fee2e2', fg: '#991b1b' },
    no_answer: { bg: '#fef3c7', fg: '#92400e' },
    busy: { bg: '#fef3c7', fg: '#92400e' },
    canceled: { bg: '#f3f4f6', fg: '#4b5563' },
    escalated: { bg: '#fce7f3', fg: '#9d174d' },
  };
  const p = palette[status] || { bg: '#e5e7eb', fg: '#374151' };
  return (
    <span
      style={{
        background: p.bg,
        color: p.fg,
        padding: '2px 8px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
      }}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
};

const CallStatusPanel: FC<{
  call: CallRow;
  pollError: string | null;
}> = ({ call, pollError }) => {
  const transcriptRef = useRef<HTMLPreElement>(null);

  const hasStructured =
    !!call.structuredData && Object.keys(call.structuredData).length > 0;

  // Auto-scroll to bottom of transcript as it grows.
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [call.transcript]);

  return (
    <div>
      <div style={styles.statusGrid}>
        <Stat label='Status'>
          <StatusPill status={call.status} />
        </Stat>
        <Stat label='Twilio'>
          <code style={styles.code}>{call.twilioStatus || '—'}</code>
        </Stat>
        <Stat label='Outcome'>{call.outcome || '—'}</Stat>
        <Stat label='Duration'>
          {call.durationSeconds
            ? `${Number(call.durationSeconds).toFixed(1)}s`
            : '—'}
        </Stat>
        <Stat label='Tools called'>{call.toolCallCount}</Stat>
        <Stat label='RAG injections'>{call.ragInjectionCount}</Stat>
        <Stat label='Prompt size'>
          {call.systemPromptLength
            ? `${call.systemPromptLength.toLocaleString()} chars`
            : '—'}
        </Stat>
        <Stat label='Connected'>
          {call.connectedAt
            ? new Date(call.connectedAt).toLocaleTimeString()
            : '—'}
        </Stat>
      </div>

      {call.twilioErrorMessage && (
        <div style={styles.error}>
          <strong>Twilio error:</strong> {call.twilioErrorMessage}
        </div>
      )}

      {pollError && (
        <div style={styles.warning}>
          <strong>Polling warning:</strong> {pollError}
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionLabel}>Transcript</div>
        <pre ref={transcriptRef} style={styles.transcript}>
          {call.transcript || '(no transcript yet — call in progress)'}
        </pre>
      </div>

      {call.summary && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Summary</div>
          <div style={styles.summary}>{call.summary}</div>
        </div>
      )}

      {call.timings && Object.keys(call.timings).length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>
            Timing waterfall (ms from WS connect)
          </div>
          <TimingWaterfall timings={call.timings} />
        </div>
      )}

      {hasStructured && (
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Structured data</div>
          <pre style={styles.structured}>
            {JSON.stringify(call.structuredData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

const Stat: FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div style={styles.statLabel}>{label}</div>
    <div style={styles.statValue}>{children}</div>
  </div>
);

// Human-readable labels for timing milestones and colour coding.
const TIMING_META: Record<
  string,
  { label: string; color: string; category: 'setup' | 'speech' | 'model' }
> = {
  twilioStreamStart: {
    label: 'Twilio stream start',
    color: '#6366f1',
    category: 'setup',
  },
  openaiWsReady: {
    label: 'OpenAI WS ready',
    color: '#8b5cf6',
    category: 'setup',
  },
  configResolved: {
    label: 'Config resolved',
    color: '#a78bfa',
    category: 'setup',
  },
  sessionCreated: {
    label: 'session.created',
    color: '#c084fc',
    category: 'setup',
  },
  sessionUpdated: {
    label: 'session.updated',
    color: '#d8b4fe',
    category: 'setup',
  },
  firstSpeechDetected: {
    label: 'First speech detected (VAD)',
    color: '#f59e0b',
    category: 'speech',
  },
  firstSpeechStopped: {
    label: 'First speech stopped (VAD)',
    color: '#fbbf24',
    category: 'speech',
  },
  firstTranscriptDone: {
    label: 'First transcript ready',
    color: '#f97316',
    category: 'speech',
  },
  firstRagStart: {
    label: 'RAG lookup start',
    color: '#ef4444',
    category: 'model',
  },
  firstRagEnd: {
    label: 'RAG lookup end',
    color: '#ef4444',
    category: 'model',
  },
  firstResponseCreate: {
    label: 'response.create sent',
    color: '#10b981',
    category: 'model',
  },
  firstAudioOut: {
    label: 'First audio byte → Twilio',
    color: '#059669',
    category: 'model',
  },
};

const TimingWaterfall: FC<{ timings: Record<string, number> }> = ({
  timings,
}) => {
  const entries = Object.entries(timings).sort(([, a], [, b]) => a - b);
  const maxMs = Math.max(...entries.map(([, v]) => v), 1);

  // Compute key deltas
  const speechStoppedToAudio =
    timings.firstAudioOut != null && timings.firstSpeechStopped != null
      ? timings.firstAudioOut - timings.firstSpeechStopped
      : null;
  const speechToAudio =
    timings.firstAudioOut != null && timings.firstTranscriptDone != null
      ? timings.firstAudioOut - timings.firstTranscriptDone
      : null;
  const ragDuration =
    timings.firstRagEnd != null && timings.firstRagStart != null
      ? timings.firstRagEnd - timings.firstRagStart
      : null;
  const responseCreateToAudio =
    timings.firstAudioOut != null && timings.firstResponseCreate != null
      ? timings.firstAudioOut - timings.firstResponseCreate
      : null;

  return (
    <div
      style={{
        background: '#0f172a',
        borderRadius: 6,
        padding: 12,
        fontSize: 12,
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
      }}
    >
      {/* Bar chart */}
      {entries.map(([key, ms]) => {
        const meta = TIMING_META[key];
        const pct = (ms / maxMs) * 100;
        return (
          <div
            key={key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 4,
            }}
          >
            <div
              style={{
                width: 180,
                color: '#94a3b8',
                textAlign: 'right',
                flexShrink: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {meta?.label || key}
            </div>
            <div
              style={{
                flex: 1,
                background: '#1e293b',
                borderRadius: 3,
                height: 16,
                position: 'relative',
              }}
            >
              <div
                style={{
                  width: `${Math.max(pct, 1)}%`,
                  background: meta?.color || '#64748b',
                  borderRadius: 3,
                  height: '100%',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <div
              style={{
                width: 60,
                color: '#e2e8f0',
                textAlign: 'right',
                flexShrink: 0,
                fontWeight: 600,
              }}
            >
              {ms}ms
            </div>
          </div>
        );
      })}

      {/* Key computed deltas */}
      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid #334155',
          display: 'flex',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        {timings.firstAudioOut != null && (
          <DeltaStat
            label='Total time to first audio'
            ms={timings.firstAudioOut}
            color='#059669'
          />
        )}
        {speechStoppedToAudio != null && (
          <DeltaStat
            label='Speech stopped → audio'
            ms={speechStoppedToAudio}
            color='#fbbf24'
          />
        )}
        {speechToAudio != null && (
          <DeltaStat
            label='Transcript → first audio'
            ms={speechToAudio}
            color='#f97316'
          />
        )}
        {ragDuration != null && (
          <DeltaStat
            label='RAG lookup (turn 1)'
            ms={ragDuration}
            color='#ef4444'
          />
        )}
        {responseCreateToAudio != null && (
          <DeltaStat
            label='response.create → audio'
            ms={responseCreateToAudio}
            color='#10b981'
          />
        )}
      </div>
    </div>
  );
};

const DeltaStat: FC<{ label: string; ms: number; color: string }> = ({
  label,
  ms,
  color,
}) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{ fontSize: 16, fontWeight: 700, color }}>
      {ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`}
    </div>
  </div>
);

// ── Styles ──────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 1280,
    margin: '0 auto',
    padding: '24px 32px 48px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: '#111827',
  },
  header: { marginBottom: 24 },
  h1: { fontSize: 24, fontWeight: 700, margin: '0 0 4px' },
  h2: { fontSize: 16, fontWeight: 600, margin: '0 0 16px' },
  subtitle: { color: '#6b7280', fontSize: 14, margin: 0 },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    marginBottom: 24,
  },
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
  },
  field: { marginBottom: 14, display: 'flex', flexDirection: 'column' },
  row: { display: 'flex', gap: 12 },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  hint: { fontSize: 12, color: '#9ca3af', marginTop: 4 },
  input: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  actions: {
    display: 'flex',
    gap: 8,
    marginTop: 16,
    paddingTop: 16,
    borderTop: '1px solid #f3f4f6',
  },
  btn: {
    padding: '10px 18px',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
  },
  btnPrimary: { background: '#111827', color: '#fff' },
  btnSecondary: { background: '#3b82f6', color: '#fff' },
  btnGhost: {
    background: '#fff',
    color: '#374151',
    border: '1px solid #d1d5db',
    padding: '6px 12px',
    fontSize: 13,
  },
  error: {
    marginTop: 12,
    padding: 12,
    background: '#fee2e2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    borderRadius: 6,
    fontSize: 13,
  },
  warning: {
    marginTop: 12,
    padding: 12,
    background: '#fef3c7',
    border: '1px solid #fde68a',
    color: '#92400e',
    borderRadius: 6,
    fontSize: 13,
  },
  emptyState: {
    color: '#9ca3af',
    fontSize: 14,
    textAlign: 'center',
    padding: '32px 16px',
  },
  statusGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginBottom: 16,
  },
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2,
  },
  statValue: { fontSize: 14, color: '#111827' },
  section: { marginTop: 16 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 6,
  },
  transcript: {
    background: '#0f172a',
    color: '#e2e8f0',
    padding: 12,
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.5,
    maxHeight: 280,
    overflow: 'auto',
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    margin: 0,
  },
  summary: {
    background: '#f9fafb',
    padding: 12,
    borderRadius: 6,
    fontSize: 13,
    lineHeight: 1.5,
  },
  structured: {
    background: '#f9fafb',
    padding: 12,
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.5,
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    overflow: 'auto',
    margin: 0,
  },
  recentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    borderBottom: '1px solid #e5e7eb',
  },
  tr: {
    borderBottom: '1px solid #f3f4f6',
  },
  td: {
    padding: '8px 10px',
    verticalAlign: 'middle',
  },
  tdEllipsis: {
    maxWidth: 320,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  code: {
    fontSize: 12,
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    background: '#f3f4f6',
    padding: '2px 6px',
    borderRadius: 4,
  },
};

export default AiCallTester;
